/**
 * MechanizationPanel — the honest mechanization status for RD-31.
 *
 * RD-31 is SETTLED and mechanized: agda/Research/AdversarialMultiAgentRobustness/
 * ViolationConfinement (Core + Quantitative) proves honest-agent isolation,
 * violation confinement, the BFT structural advantage, and a quantitative
 * violation-surface bound under --cubical-compatible --safe with zero
 * postulates (registry fidelity gaps FG-AMR-1..4 COMPLETE). Two refinements
 * remain open: the tight closed-form bound and the dynamic-adversary model.
 * The panel reads its content from data/theorems.json.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */
import React from "react";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import { AgdaSourceLink, CiteLinks } from "@csk/demo-ui";
import theoremsData from "../data/theorems.json" with { type: "json" };
function fileName(path: string): string {
  return path.split("/").slice(-2).join("/");
}
export function MechanizationPanel(): React.JSX.Element {
  return (
    <PanelSurface.PanelShell
      title="Mechanization"
      subtitle="Honest status: RD-31's core theorems are machine-checked in Agda (--safe, zero postulates); two quantitative refinements remain open."
      accentColor={panelTokens.accentTeal}
    >
      <PanelSurface.Callout title={theoremsData.honestStatus.title} tone="info">
        {theoremsData.honestStatus.note}
      </PanelSurface.Callout>

      <PanelSurface.SectionHeading>Status at a glance</PanelSurface.SectionHeading>
      <PanelSurface.KpiGrid>
        <PanelSurface.KpiBox
          label="RD-31 Agda modules"
          value="2"
          color={panelTokens.honest}
          subtitle="ViolationConfinement: Core + Quantitative"
        />
        <PanelSurface.KpiBox
          label="Fidelity gaps closed"
          value="4 / 4"
          color={panelTokens.honest}
          subtitle="FG-AMR-1..4 COMPLETE (registry)"
        />
        <PanelSurface.KpiBox
          label="Postulates"
          value="0"
          color={panelTokens.honest}
          subtitle="--cubical-compatible --safe"
        />
        <PanelSurface.KpiBox
          label="Open refinements"
          value="2"
          color={panelTokens.surfaceBound}
          subtitle="tight closed-form bound · dynamic adversary"
        />
      </PanelSurface.KpiGrid>

      <PanelSurface.SectionHeading>What is proved (and what remains)</PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.StatusList
          ariaLabel="RD-31 mechanization status"
          items={theoremsData.theorems.map((theorem) => {
            const mechanized = theorem.status.includes("MECHANIZED");
            return {
              id: theorem.id,
              title: `${theorem.id} ${theorem.name}`,
              status: theorem.status,
              tone: mechanized ? ("complete" as const) : ("blocked" as const),
              color: mechanized ? panelTokens.honest : panelTokens.surfaceBound,
              description: `${theorem.basis} — ${theorem.agdaName} in ${fileName(theorem.agdaFile)}.`,
            };
          })}
        />
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>What is still open</PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.Prose className="m-0">
          Two refinements are flagged honestly as not-yet-closed. The{" "}
          <strong className="text-[var(--adv-bright)]">tight</strong>{" "}
          closed-form violation-surface bound{" "}
          <code className="font-mono">O(k·a³ + k²·a²·A)</code> is the game's
          runtime read-out, but the Agda proves the finite-universe bound and
          the honest-surface-zero result, not the tight form in k, a, A. And the{" "}
          <strong className="text-[var(--adv-bright)]">
            dynamic adversary
          </strong>{" "}
          model — agents compromised at runtime — is noted as a proof obligation
          in the Quantitative module, not yet discharged.
        </PanelSurface.Prose>
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>What the game's rules are</PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.Prose className="m-0">
          The rules in <code className="font-mono">algorithms/rules.ts</code>{" "}
          and the reducer in{" "}
          <code className="font-mono">algorithms/model.ts</code> encode the
          mechanized RD-31 structure — the honest-to-honest move is never
          generated, every realized square factors through an adversary, and the
          realized surface stays under the §4.4 estimate. The tests in{" "}
          <code className="font-mono">tests/rules.test.ts</code> pin these
          invariants of the model. The proofs of record are the Agda theorems
          above; this game is an interactive demonstration of them, not a
          substitute for them.
        </PanelSurface.Prose>
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>Naming triad</PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.FactList
          ariaLabel="RD-31 naming triad"
          items={[
            {
              id: "security",
              label: "Security",
              value: theoremsData.namingTriad.security,
            },
            {
              id: "governance",
              label: "Governance",
              value: theoremsData.namingTriad.governance,
            },
            {
              id: "mathematical",
              label: "Mathematical",
              value: theoremsData.namingTriad.mathematical,
            },
          ]}
        />
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>Open in the repository</PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <AgdaSourceLink module="agda/Research/AdversarialMultiAgentRobustness/ViolationConfinement/Core.agda" />
        <AgdaSourceLink module="agda/Research/AdversarialMultiAgentRobustness/ViolationConfinement/Quantitative.agda" />
        <CiteLinks
          items={[
            {
              label: "RD-31 manifest (Agda module map)",
              path: "agda/Research/AdversarialMultiAgentRobustness/RD-31-Manifest.md",
            },
            {
              label: "RD-31 research direction (§4–§5)",
              path: "research/settled/RD-31-ADVERSARIAL-MULTI-AGENT-ROBUSTNESS.md",
            },
            {
              label: "RD-05 Multi-Agent Composition (the mechanized basis)",
              path: "research/settled/RD-05-MULTI-AGENT-COMPOSITION.md",
            },
            {
              label: "Multi-Agent Safety — deep technical analysis",
              path: "theory/structural/MULTI-AGENT-SAFETY.md",
            },
          ]}
        />
      </PanelSurface.Card>
    </PanelSurface.PanelShell>
  );
}
